'use client';

import {
	type FC,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import algoliasearch from 'algoliasearch';
import clsx from 'clsx';
import {useSearchParams} from 'next/navigation';

import {algoliaSearchOnlyKey, publicAlgoliaAppId} from '@/utils/algolia.api';
import {i18n} from '@/utils/i18n';
import {dataset} from '@/utils/sanity.api';

import {
	DOCUMENT_TYPE_ARTICLE,
	DOCUMENT_TYPE_CATEGORY,
	DOCUMENT_TYPE_FRONTPAGE,
	DOCUMENT_TYPE_PAGE,
} from '../../schema/documents/constants';

import {SearchResults} from './search-results';

const documentTypeFilters = [
	{
		type: DOCUMENT_TYPE_ARTICLE,
	},
	{
		type: DOCUMENT_TYPE_CATEGORY,
	},
	{
		type: DOCUMENT_TYPE_PAGE,
	},
	{
		type: DOCUMENT_TYPE_FRONTPAGE,
	},
] as const;

export const documentTypes = documentTypeFilters.map((filter) => filter.type);

export type DocumentType = (typeof documentTypes)[number];

export const SearchClient: FC = () => {
	const [algoliaHits, setAlgoliaHits] = useState<
		{_type: string; objectID: string}[]
	>([]);
	const [algoliaCounts, setAlgoliaCounts] = useState(
		new Map<DocumentType, number>(),
	);

	const searchParams = useSearchParams();

	const triggerFetch = useCallback(async (search: string) => {
		const results = await fetchAlgoliaResults(search);
		setAlgoliaHits(results.hits);
		setAlgoliaCounts(results.counts);
	}, []);

	useEffect(() => {
		const searchParam = searchParams.get('q');

		if (!searchParam) {
			return;
		}

		// FIXME: fix react hook error
		// eslint-disable-next-line react-hooks/set-state-in-effect
		triggerFetch(searchParam);
	}, [searchParams, triggerFetch]);

	const hasSearchResults = useMemo(() => {
		return Array.from(algoliaCounts.values()).some((count) => count > 0);
	}, [algoliaCounts]);

	return (
		<div>
			{!hasSearchResults || algoliaHits?.length === 0 ? (
				<div
					className={clsx('text-pMedium', 'italic', 'mb-10')}
					aria-live="polite"
				>
					{i18n.components.searchPage.noResults}
				</div>
			) : (
				<>
					<div
						className={clsx('text-pMedium', 'mb-10')}
						aria-live="polite"
					>
						{`${i18n.components.searchPage.yourSearch} ${algoliaHits.length}${' '}
						${i18n.components.searchPage.hits}`}
					</div>
					<div className={clsx('text-xl', 'mb-3')}>
						{i18n.components.searchPage.viewing} 1&mdash;
						{algoliaHits.length} {i18n.components.searchPage.of}{' '}
						{algoliaHits.length} {i18n.components.searchPage.hits}
					</div>
				</>
			)}

			{hasSearchResults && algoliaHits.length > 0 && (
				<Suspense fallback={null}>
					<SearchResults hits={algoliaHits} />
				</Suspense>
			)}
		</div>
	);
};

const fetchAlgoliaResults = async (query: string) => {
	const counts = new Map<DocumentType, number>();

	const client = algoliasearch(publicAlgoliaAppId, algoliaSearchOnlyKey);

	const index = client.initIndex(dataset);

	const searchResults = await index.search<{_type: DocumentType}>(query, {
		attributesToRetrieve: ['objectID', '_type'],
		attributesToHighlight: [],
		hitsPerPage: 1000,
	});

	const hits = searchResults.hits;
	searchResults.hits.forEach((hit) => {
		if (documentTypes.includes(hit._type)) {
			counts.set(hit._type, (counts.get(hit._type) || 0) + 1);
		}
	});

	return {
		hits,
		counts,
	};
};
